import React, { useState, useCallback, useEffect } from 'react';
import { SearchBar } from './components/SearchBar';
import { SearchHistory } from './components/SearchHistory';
import { HistoryView } from './components/HistoryView';
import { CardGrid } from './components/CardGrid';
import { LockScreen } from './components/LockScreen';
import { NotAuthorized } from './components/NotAuthorized';
import { DevLockScreen } from './components/DevLockScreen';
import { DevPanel } from './components/DevPanel';
import './styles.css';

type FilterType = 'all' | 'website' | 'no-website';

interface SearchData {
  all: any[];
  noSite: any[];
}

export const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [searchData, setSearchData] = useState<SearchData>({ all: [], noSite: [] });
  // Initialize checks immediately to prevent render flash
  // Robust check for /dev path, handling potential trailing slashes
  const [isDevMode, setIsDevMode] = useState(() => {
    const path = window.location.pathname.toLowerCase().replace(/\/$/, '');
    return path === '/dev';
  });
  const [isDevAuthenticated, setIsDevAuthenticated] = useState(false);

  useEffect(() => {
    // Double check on mount in case of any routing edge cases
    const path = window.location.pathname.toLowerCase().replace(/\/$/, '');
    if (path === '/dev' && !isDevMode) {
      setIsDevMode(true);
    }

    if (isDevMode || path === '/dev') {
      const devAuth = sessionStorage.getItem('dev_auth');
      if (devAuth === 'true') {
        setIsDevAuthenticated(true);
      }
    } else {
      const auth = sessionStorage.getItem('site_auth');
      if (auth === 'true') {
        setIsAuthenticated(true);
      }
    }
  }, [isDevMode]);

  const handleAuthenticated = () => {
    setIsAuthenticated(true);
    sessionStorage.setItem('site_auth', 'true');
  };

  const handleDevAuthenticated = () => {
    setIsDevAuthenticated(true);
    sessionStorage.setItem('dev_auth', 'true');
  };

  const handleUnauthorized = () => {
    setIsUnauthorized(true);
  };

  const [loading, setLoading] = useState(false);
  const [currentQuery, setCurrentQuery] = useState('');
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [showHistory, setShowHistory] = useState(false);

  const performSearch = useCallback(async (query: string) => {
    setShowHistory(false);
    setLoading(true);
    setError('');
    setCurrentQuery(query);
    setFilter('all');
    
    try {
      const response = await fetch(`/api/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();

      if (data.success) {
        setSearchData({
          all: data.all || [],
          noSite: data.noSite || []
        });
        // Refresh history list after successful search
        setHistoryRefresh(prev => prev + 1);
      } else {
        setError('Search failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredResults = React.useMemo(() => {
    if (filter === 'website') {
      return searchData.all.filter(item => item.website);
    }
    if (filter === 'no-website') {
      return searchData.all.filter(item => !item.website);
    }
    return searchData.all;
  }, [searchData.all, filter]);

  const handleExport = useCallback(() => {
    if (!filteredResults.length) return;

    const headers = ['S.No', 'Name', 'Full Address', 'Phone Number', 'Website', 'Rating', 'No of Reviews'];
    
    const csvContent = [
      headers.join(','),
      ...filteredResults.map((item, index) => {
        const escapeCsv = (str: string) => `"${(str || '').replace(/"/g, '""')}"`;
        const row = [
          index + 1,
          escapeCsv(item.title),
          escapeCsv(item.address),
          escapeCsv(item.phoneNumber),
          item.website ? 'Yes' : 'No',
          item.rating || '',
          item.ratingCount || 0
        ];
        return row.join(',');
      })
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `business_results_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filteredResults]);

  if (isUnauthorized) {
    return <NotAuthorized />;
  }

  // Developer Panel Route
  if (isDevMode) {
    if (!isDevAuthenticated) {
      return <DevLockScreen onAuthenticated={handleDevAuthenticated} />;
    }
    return <DevPanel />;
  }

  // Main App Route
  if (!isAuthenticated) {
    return <LockScreen onAuthenticated={handleAuthenticated} onUnauthorized={handleUnauthorized} />;
  }

  // Full History View
  if (showHistory) {
    return (
      <HistoryView
        onSelect={performSearch}
        onBack={() => setShowHistory(false)}
        refreshTrigger={historyRefresh}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Business Finder
          </h1>
          <p className="text-gray-600">Search for businesses in your area</p>
        </div>

        {/* Search Bar */}
        <SearchBar onSearch={performSearch} loading={loading} />

        {/* Recent Searches */}
        <SearchHistory onSelect={performSearch} onViewAll={() => setShowHistory(true)} refreshTrigger={historyRefresh} />

        {/* Results Section */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
            {error}
          </div>
        )}

        {currentQuery && (
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Results for "{currentQuery}"
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                Found {filteredResults.length} businesses
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex p-1 bg-white border border-gray-200 rounded-xl shadow-sm">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    filter === 'all'
                      ? 'bg-blue-50 text-blue-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter('website')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    filter === 'website'
                      ? 'bg-blue-50 text-blue-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  With Website
                </button>
                <button
                  onClick={() => setFilter('no-website')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    filter === 'no-website'
                      ? 'bg-blue-50 text-blue-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  No Website
                </button>
              </div>

              <button
                onClick={handleExport}
                disabled={loading}
                className={`flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-xl shadow-sm transition-all duration-200 ${
                  loading
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-green-700 hover:shadow-md'
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export Excel
              </button>
            </div>
          </div>
        )}

        <CardGrid results={filteredResults} loading={loading} />
      </div>
    </div>
  );
};

export default App;
